import React, { useCallback, useState } from 'react';
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import productoService, { OPTIONS_PRODUCTO_OPERATIVO } from '../../services/productoService';
import { getApiErrorMessage } from '../../utils/apiError';
import RecetaArticuloSearchDialog, {
  mapProductoToRecetaArticulo,
  RecetaArticuloSeleccionado,
} from './RecetaArticuloSearchDialog';

export type RecetaArticuloSuggestion = RecetaArticuloSeleccionado;

interface Props {
  label: string;
  codigo: string;
  descripcion: string;
  disabled?: boolean;
  required?: boolean;
  tiposArticulo?: string;
  mensajeTipoInvalido?: string;
  dialogTitle?: string;
  onResolved: (item: RecetaArticuloSeleccionado) => void;
  onCodigoChange?: (codigo: string) => void;
  onDescripcionChange?: (descripcion: string) => void;
}

const tiposPermitidosSet = (tiposArticulo?: string): Set<string> | null => {
  if (!tiposArticulo?.trim()) return null;
  return new Set(
    tiposArticulo
      .split(',')
      .map((t) => t.trim().toUpperCase())
      .filter(Boolean),
  );
};

const RecetaProductoLookup: React.FC<Props> = ({
  label,
  codigo,
  descripcion,
  disabled,
  required,
  tiposArticulo,
  mensajeTipoInvalido = 'El artículo no tiene el tipo requerido',
  dialogTitle,
  onResolved,
  onCodigoChange,
  onDescripcionChange,
}) => {
  const [resolving, setResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const tiposSet = tiposPermitidosSet(tiposArticulo);

  const cumpleTipo = useCallback(
    (tipo: string | null | undefined): boolean => {
      if (!tiposSet) return true;
      return tiposSet.has(String(tipo ?? '').trim().toUpperCase());
    },
    [tiposSet],
  );

  const resolveExact = useCallback(
    async (raw: string) => {
      const trimmed = raw.trim();
      if (!trimmed) return;
      setResolving(true);
      setError(null);
      try {
        const producto = await productoService.getProducto(trimmed, OPTIONS_PRODUCTO_OPERATIVO);
        if (!cumpleTipo(producto.tipoArticulo)) {
          setError(mensajeTipoInvalido);
          setDialogOpen(true);
          return;
        }
        onResolved(mapProductoToRecetaArticulo(producto));
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        setError(
          status === 409
            ? getApiErrorMessage(err, 'Artículo no disponible')
            : 'No existe en el maestro de artículos',
        );
        setDialogOpen(true);
      } finally {
        setResolving(false);
      }
    },
    [onResolved, cumpleTipo, mensajeTipoInvalido],
  );

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          label={`${label} — código`}
          value={codigo}
          required={required}
          disabled={disabled || resolving}
          error={Boolean(error)}
          helperText={error || ' '}
          onChange={(e) => {
            setError(null);
            onCodigoChange?.(e.target.value);
            if (descripcion) onDescripcionChange?.('');
          }}
          onBlur={(e) => {
            if (e.target.value.trim() && !descripcion.trim()) {
              void resolveExact(e.target.value);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void resolveExact((e.target as HTMLInputElement).value);
            }
            if (e.key === 'F4' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l')) {
              e.preventDefault();
              setDialogOpen(true);
            }
          }}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {resolving ? (
                  <CircularProgress size={16} />
                ) : (
                  <Tooltip title="Buscar artículo (F4)">
                    <span>
                      <IconButton
                        size="small"
                        edge="end"
                        disabled={disabled}
                        onClick={() => setDialogOpen(true)}
                        aria-label="Buscar artículo"
                      >
                        <SearchIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </InputAdornment>
            ),
          }}
          sx={{ '& input': { fontFamily: 'monospace' } }}
        />
        <TextField
          label={`${label} — descripción`}
          value={descripcion}
          disabled={disabled}
          onChange={(e) => onDescripcionChange?.(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'F4' || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l')) {
              e.preventDefault();
              setDialogOpen(true);
            }
          }}
          fullWidth
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title="Buscar artículo (F4)">
                  <span>
                    <IconButton
                      size="small"
                      edge="end"
                      disabled={disabled}
                      onClick={() => setDialogOpen(true)}
                      aria-label="Buscar artículo"
                    >
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <RecetaArticuloSearchDialog
        open={dialogOpen}
        title={dialogTitle ?? `Buscar ${label.toLowerCase()}`}
        tiposArticulo={tiposArticulo}
        initialSearchTerm={codigo || descripcion}
        onClose={() => setDialogOpen(false)}
        onSelect={(item) => {
          setError(null);
          onResolved(item);
        }}
      />
    </>
  );
};

export default RecetaProductoLookup;
